import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { FormLabel } from '@/components/ui/form';
import { useToast } from '@/components/ui/hooks/use-toast';
import { buildNameLikeSelectOptions, comboBoxOptionsOf, selectOption } from '@/components/ui/multi-select-utils';
import { useAllSchools } from '@/lib/api-call-hooks';
import { createStudentProfile, createUser, getUsers, updateUserRoles } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { CreateUserCommonForm } from '@/routes/_layout/users/-components/create-user-common-form';
import {
  createUserCommonFormSchema,
  createUserOptionsFromFormData,
} from '@/routes/_layout/users/-components/form-schema';
import { studentOfSchool } from '@airreading/common';
import {
  Role,
  UpdateStudentOptions,
  UserWithRolesAndProfiles,
  studentYearOption,
  studentYearSelectableSchema,
  studentYearValues,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

const formSchema = createUserCommonFormSchema.extend({
  schoolId: selectOption.refine(({ value }) => !!value, { message: 'School is required' }),
  grade: studentYearSelectableSchema.optional(),
  specialEd: z.boolean().default(false),
  dyslexic: z.boolean().default(false),
  ell: z.boolean().default(false),
});
type Form = z.infer<typeof formSchema>;

export interface CreateStudentProps {
  trigger?: ReactNode;
  fromUser?: UserWithRolesAndProfiles;
  onSuccess?: () => void;
  schoolId?: string;
}

export const CreateStudent = ({ trigger, fromUser, onSuccess, schoolId = '' }: CreateStudentProps) => {
  const { mutateAsync: createStudentProfileMutate } = useMutation({
    mutationFn: createStudentProfile,
  });

  const { mutateAsync: createUserMutate } = useMutation({
    mutationFn: createUser,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: schools, isPending: isLoadingSchools } = useAllSchools();

  const onSubmit = ({ formData, closeModal }: SubmitProps<Form>) => {
    const { schoolId, grade, specialEd, dyslexic, ell } = formData;
    const createUserPayload = createUserOptionsFromFormData(formData);
    const studentsOfSchool = studentOfSchool(schoolId.value);
    createUserPayload.assignRoles = [studentsOfSchool];

    const studentProfilePayload: UpdateStudentOptions = {
      grade: grade === 'null' ? null : grade,
      specialEd,
      dyslexic,
      ell,
    };

    const apiCall = fromUser
      ? () =>
          createStudentProfileMutate({ userId: fromUser.id, data: studentProfilePayload }).then(() => {
            return updateUserRoles({
              userId: fromUser.id,
              roles: [...fromUser.userRoles, studentsOfSchool],
            });
          })
      : () =>
          createUserMutate({
            ...createUserPayload,
            studentProfile: studentProfilePayload,
          });

    return apiCall()
      .then(() => {
        toast({
          title: 'Student created successfully',
        });
        onSuccess?.();
        closeModal();
        queryClient.invalidateQueries(getUsers());
      })
      .catch((error) => {
        const airServerError = airServerErrorFrom(error);
        const errorMessage = airServerError?.userFacingMessage || 'Student creation failed';

        toast({
          title: 'Student creation failed',
          description: errorMessage,
        });
      });
  };

  const selectableSchools = (schools ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(buildNameLikeSelectOptions);
  const defaultSchool = schoolId ? (schools ?? []).find((s) => s.id === schoolId) : undefined;

  return (
    <FormModal
      modalTitle="Create Student"
      dialogContentProps={{ className: 'max-w-3xl' }}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Create student</Button>}
      defaultValues={{
        ...(fromUser
          ? {
              firstName: fromUser.firstName || undefined,
              lastName: fromUser.lastName || undefined,
              email: fromUser.email || undefined,
              specialEd: fromUser.studentProfile?.specialEd || false,
              dyslexic: fromUser.studentProfile?.dyslexic || false,
              ell: fromUser.studentProfile?.ell || false,
            }
          : {}),
        ...(defaultSchool && {
          schoolId: buildNameLikeSelectOptions(defaultSchool),
        }),
      }}
      confirmBtnLabel="Add"
    >
      {({ form }) => {
        return (
          <div className="grid grid-cols-2 gap-2 py-4">
            <CreateUserCommonForm form={form} disabled={!!fromUser} role={Role.student} />
            <FormSingleSelect
              name="grade"
              label="Grade"
              form={form}
              options={studentYearValues}
              renderOption={studentYearOption}
              triggerProps={{ className: 'w-[80%]' }}
            />
            <FormComboBox
              name="schoolId"
              label="School"
              form={form}
              required={true}
              {...comboBoxOptionsOf(selectableSchools)}
              renderOptionSelected={(option) => (option ? option.label : 'Select a school')}
              emptySearchOptionMessage="No schools found"
              triggerProps={{ className: 'w-[80%]', disabled: !!schoolId || isLoadingSchools }}
            />
            <div className="flex flex-col justify-center gap-1">
              <FormLabel className="font-bold">Special needs</FormLabel>
              <FormCheckbox horizontal name="ell" label="Emergent Bilingual" form={form} />
              <FormCheckbox horizontal name="specialEd" label="Special Ed" form={form} />
              <FormCheckbox horizontal name="dyslexic" label="Dyslexic" form={form} />
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
